import React, { useEffect, useCallback, useMemo } from "react";
import "./viewBlog.css";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { ViewBlog } from "../../Redux/Actions/blogActions";
import MainLayout from "../../Layout/mainLayout";

const ViewBlogPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { blog, loading } = useSelector((state) => state.blog);
  const data = blog;

  useEffect(() => {
    if (!id) return;
    if (blog?._id === id) return;

    dispatch(ViewBlog(id)).catch(() => {
      toast.error("Failed to load blog details.");
    });
  }, [id, dispatch]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  const coverImages = useMemo(() => {
    if (!data?.cover_images) return [];
    if (Array.isArray(data.cover_images)) return data.cover_images;
    return data.cover_images
      .split(",")
      .map((img) => img.trim())
      .filter(Boolean);
  }, [data]);

  const blogSections = useMemo(() => {
    if (!data?.blog_content) return [];
    if (Array.isArray(data.blog_content)) return data.blog_content;
    return [];
  }, [data]);

  const authors = useMemo(() => {
    if (!data?.author) return [];
    if (Array.isArray(data.author)) return data.author;
    return [];
  }, [data]);

  if (loading) {
    return (
      <MainLayout>
        <div className="vbg-loader-wrap">
          <div className="vbg-loader" />
          <p className="vbg-loader-text">Loading blog details…</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="vbg-wrapper">
        <div className="vbg-header">
          <div className="vbg-header-left">
            <Link to="/blog" className="vbg-back-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </Link>
            <div className="vbg-header-divider" />
            <div>
              <div className="vbg-breadcrumb">Blog / List / View</div>
              <h1 className="vbg-title">Blog Details</h1>
            </div>
          </div>
        </div>

        {data && (
          <>
            <div className="vbg-hero-card">
              <div className="vbg-hero-left">
                <span className="vbg-hero-label">Blog Title</span>
                <span className="vbg-hero-value">{data.title || "—"}</span>
              </div>
              <div className="vbg-hero-meta">
                <div className="vbg-hero-meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  {formatDate(data.created_at)}
                </div>
a
                {data.category && (
                  <span className="vbg-category-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                    {data.category.replace("_", " ")}
                  </span>
                )}
              </div>
            </div>

            <div className="vbg-grid-layout">
              <div className="vbg-col">
                <div className="vbg-card">
                  <div className="vbg-card-header">
                    <span className="vbg-card-badge">INFO</span>
                    <h3 className="vbg-card-title">Blog Information</h3>
                  </div>

                  <div className="vbg-info-grid">
                    <div className="vbg-info-item">
                      <span className="vbg-info-label">Category</span>
                      <span className="vbg-info-value">{data.category?.replace("_", " ") || "—"}</span>
                    </div>

                    <div className="vbg-info-item">
                      <span className="vbg-info-label">Created At</span>
                      <span className="vbg-info-value">{formatDate(data.created_at)}</span>
                    </div>


                    <div className="vbg-info-item">
                      <span className="vbg-info-label">Cover Images</span>
                      <span className="vbg-info-value">{coverImages.length} uploaded</span>
                    </div>

                    <div className="vbg-info-item">
                      <span className="vbg-info-label">Sections</span>
                      <span className="vbg-info-value">{blogSections.length}</span>
                    </div>

                    <div className="vbg-info-item">
                      <span className="vbg-info-label">Authors</span>
                      <span className="vbg-info-value">{authors.length}</span>
                    </div>
                  </div>
                </div>

                <div className="vbg-card">
                  <div className="vbg-card-header">
                    <span className="vbg-card-badge">DESC</span>
                    <h3 className="vbg-card-title">Short Description</h3>
                  </div>
                  <p className="vbg-description">{data.description || "No description provided."}</p>
                </div>

                <div className="vbg-card">
                  <div className="vbg-card-header">
                    <span className="vbg-card-badge">AUTHORS</span>
                    <h3 className="vbg-card-title">Author Details</h3>
                  </div>

                  {authors.length > 0 ? (
                    <div className="vbg-authors-list">
                      {authors.map((author, index) => (
                        <div key={index} className="vbg-author-item">
                          {author.author_image && (
                            <div className="vbg-author-image-wrap">
                              <img
                                src={author.author_image}
                                alt={author.author_name || `author-${index + 1}`}
                                className="vbg-author-image"
                              />
                            </div>
                          )}

                          <div className="vbg-author-content">
                            <h4 className="vbg-author-name">{author.author_name || "Unnamed Author"}</h4>
                            <p className="vbg-author-bio">{author.author_bio || "No bio available."}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="vbg-no-data">No authors available.</p>
                  )}
                </div>
              </div>

              <div className="vbg-col">
                <div className="vbg-card">
                  <div className="vbg-card-header">
                    <span className="vbg-card-badge">CONTENT</span>
                    <h3 className="vbg-card-title">Blog Content</h3>
                  </div>

                  {blogSections.length > 0 ? (
                    <div className="vbg-sections-list">
                      {blogSections.map((section, index) => (
                        <div key={index} className="vbg-section-item">
                          <div className="vbg-section-head">
                            <span className="vbg-section-badge">Section {index + 1}</span>
                          </div>

                          <div
                            className="vbg-section-html"
                            dangerouslySetInnerHTML={{
                              __html: section.content || "<p>No content available.</p>",
                            }}
                          />

                          {section.image && (
                            <div className="vbg-section-image-wrap">
                              <img
                                src={section.image}
                                alt={`section-${index + 1}`}
                                className="vbg-section-image"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="vbg-no-data">No content available.</p>
                  )}
                </div>

                <div className="vbg-card">
                  <div className="vbg-card-header">
                    <span className="vbg-card-badge">IMAGES</span>
                    <h3 className="vbg-card-title">Cover Images</h3>
                  </div>

                  {coverImages.length > 0 ? (
                    <div className="vbg-images-grid">
                      {coverImages.map((url, index) => (
                        <div key={index} className="vbg-image-wrap">
                          <img src={url} alt={`cover-${index + 1}`} className="vbg-image" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="vbg-no-data">No cover images uploaded.</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {!data && !loading && (
          <p className="vbg-no-data">Blog not found.</p>
        )}
      </div>
    </MainLayout>
  );
};

export default ViewBlogPage;